import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import '../../v2-theme.css';
import './AdminDashboard.css';

/**
 * Admin Dashboard
 * Matches admin-dashboard.html exactly
 * Tabs: Overview, Users, Shifts, Subscriptions, Disputes, Reviews, Settings
 */
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [adminUser, setAdminUser] = useState(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeShifts: 0,
    totalRevenue: 0,
    newUsers: 0
  });
  const [users, setUsers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check admin authentication
    const adminToken = localStorage.getItem('admin_token');
    const adminData = localStorage.getItem('admin_user');

    if (!adminToken || !adminData) {
      navigate('/admin-login');
      return;
    }

    setAdminUser(JSON.parse(adminData));

    // Set default auth header for admin requests
    api.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;

    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Get admin token for each request
      const adminToken = localStorage.getItem('admin_token');
      const config = {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      };

      // Fetch stats, users, shifts, subscriptions in parallel
      const [statsRes, usersRes, shiftsRes] = await Promise.all([
        api.get('/admin/stats', config).catch(() => ({ data: stats })),
        api.get('/admin/users', config).catch(() => ({ data: { users: [] } })),
        api.get('/admin/jobs', { ...config, params: { status: 'all' } }).catch(() => ({ data: { jobs: [] } }))
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setShifts(shiftsRes.data.jobs || []);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      delete api.defaults.headers.common['Authorization'];
      navigate('/admin-login');
    }
  };

  const handleGrantProfessional = async (userId) => {
    if (!window.confirm('Grant Professional subscription to this worker?')) return;

    try {
      const adminToken = localStorage.getItem('admin_token');
      await api.post(`/admin/grant-professional/${userId}`, { grant: true }, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      alert('Professional subscription granted successfully!');
      fetchDashboardData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to grant subscription');
    }
  };

  const handleGrantUnlimited = async (userId) => {
    if (!window.confirm('Grant Unlimited subscription to this restaurant?')) return;

    try {
      const adminToken = localStorage.getItem('admin_token');
      await api.post(`/admin/grant-unlimited/${userId}`, { grant: true }, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      alert('Unlimited subscription granted successfully!');
      fetchDashboardData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to grant subscription');
    }
  };

  if (loading && !adminUser) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="admin-header">
        <div className="header-left">
          <img src="/hotf-web-icon.png" alt="Logo" className="logo" />
          <div className="site-title">Hands On The Fly</div>
          <div className="admin-badge">Admin</div>
        </div>
        <div className="header-right">
          <div className="admin-user">👤 {adminUser?.username || 'Admin'}</div>
          <button className="logout-btn" onClick={handleLogout}>Logout</button>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="admin-nav">
        <button
          className={`nav-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`nav-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button
          className={`nav-tab ${activeTab === 'shifts' ? 'active' : ''}`}
          onClick={() => setActiveTab('shifts')}
        >
          Shifts
        </button>
        <button
          className={`nav-tab ${activeTab === 'subscriptions' ? 'active' : ''}`}
          onClick={() => setActiveTab('subscriptions')}
        >
          Subscriptions
        </button>
      </nav>

      {/* Main Content */}
      <main className="admin-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <section className="admin-section">
            <h1>Dashboard Overview</h1>

            {/* Stats Grid */}
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">Total Users</div>
                <div className="stat-value">{stats.totalUsers || users.length}</div>
                <div className="stat-change positive">
                  ↑ {stats.newUsers || 0} this week
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-label">Active Shifts</div>
                <div className="stat-value">{shifts.filter(s => s.status === 'open' || s.status === 'filled').length}</div>
                <div className="stat-change">
                  {shifts.filter(s => s.status === 'open').length} open
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-label">Workers</div>
                <div className="stat-value">{users.filter(u => u.user_type === 'worker').length}</div>
              </div>

              <div className="stat-card">
                <div className="stat-label">Restaurants</div>
                <div className="stat-value">{users.filter(u => u.user_type === 'restaurant').length}</div>
              </div>
            </div>

            {/* Quick Actions */}
            <h2 className="section-subtitle">Quick Actions</h2>
            <div className="quick-actions">
              <button className="action-btn" onClick={() => setActiveTab('users')}>
                Manage Users
              </button>
              <button className="action-btn" onClick={() => setActiveTab('shifts')}>
                View All Shifts
              </button>
              <button className="action-btn" onClick={() => setActiveTab('subscriptions')}>
                Grant Subscriptions
              </button>
              <button className="action-btn" onClick={fetchDashboardData}>
                Refresh Data
              </button>
            </div>

            {/* Recent Activity */}
            <div className="data-section">
              <div className="section-header">
                <h3 className="section-title">Recent Shifts</h3>
              </div>
              <div className="recent-list">
                {shifts.slice(0, 5).map(shift => (
                  <div key={shift.id} className="recent-item">
                    <div>
                      <strong>{shift.position}</strong> - {shift.restaurant_profiles?.business_name}
                    </div>
                    <div className="item-meta">
                      {shift.shift_date} • ${shift.hourly_rate}/hr • {shift.status}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <section className="admin-section">
            <h1>User Management</h1>

            <div className="data-section">
              <div className="section-header">
                <h3 className="section-title">All Users ({users.length})</h3>
                <div className="filter-bar">
                  <input
                    type="text"
                    placeholder="Search users..."
                    className="search-input"
                  />
                  <select className="filter-select">
                    <option value="all">All Types</option>
                    <option value="worker">Workers</option>
                    <option value="restaurant">Restaurants</option>
                  </select>
                </div>
              </div>

              <div className="table-wrapper">
                <table className="user-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Type</th>
                      <th>Phone</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.id}>
                        <td>{user.email}</td>
                        <td>
                          <span className={`status-badge status-${user.user_type}`}>
                            {user.user_type}
                          </span>
                        </td>
                        <td>{user.phone || 'N/A'}</td>
                        <td>{new Date(user.created_at).toLocaleDateString()}</td>
                        <td>
                          {user.user_type === 'worker' && (
                            <button
                              className="table-action-btn"
                              onClick={() => handleGrantProfessional(user.id)}
                            >
                              Grant Pro
                            </button>
                          )}
                          {user.user_type === 'restaurant' && (
                            <button
                              className="table-action-btn"
                              onClick={() => handleGrantUnlimited(user.id)}
                            >
                              Grant Unlimited
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {/* Shifts Tab */}
        {activeTab === 'shifts' && (
          <section className="admin-section">
            <h1>Shift Management</h1>

            <div className="data-section">
              <div className="section-header">
                <h3 className="section-title">All Shifts ({shifts.length})</h3>
                <div className="filter-bar">
                  <select className="filter-select">
                    <option value="all">All Status</option>
                    <option value="open">Open</option>
                    <option value="filled">Filled</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>

              <div className="table-wrapper">
                <table className="user-table">
                  <thead>
                    <tr>
                      <th>Position</th>
                      <th>Restaurant</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Rate</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shifts.map(shift => (
                      <tr key={shift.id}>
                        <td><strong>{shift.position}</strong></td>
                        <td>{shift.restaurant_profiles?.business_name || 'Unknown'}</td>
                        <td>{shift.shift_date}</td>
                        <td>{shift.start_time} - {shift.end_time}</td>
                        <td>${shift.hourly_rate}/hr</td>
                        <td>
                          <span className={`status-badge status-${shift.status}`}>
                            {shift.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && (
          <section className="admin-section">
            <h1>Subscription Management</h1>

            <div className="data-section">
              <div className="section-header">
                <h3 className="section-title">Grant Subscriptions</h3>
              </div>

              <p style={{ marginBottom: '20px', color: 'var(--silver)' }}>
                Use the Users tab to grant Professional or Unlimited subscriptions to users.
              </p>

              <div className="quick-actions">
                <button className="action-btn" onClick={() => setActiveTab('users')}>
                  Go to Users
                </button>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
